import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Link,
    Box,
    Chip,
    Tooltip,
    IconButton,
} from '@mui/material';
import { Receipt as ReceiptIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { format, formatDistanceToNow } from 'date-fns';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 'bold',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
    const statusColors = {
        paid: {
            bg: theme.palette.success.light,
            color: theme.palette.success.dark,
        },
        pending: {
            bg: theme.palette.warning.light,
            color: theme.palette.warning.dark,
        },
        failed: {
            bg: theme.palette.error.light,
            color: theme.palette.error.dark,
        },
    };

    return {
        backgroundColor: statusColors[status]?.bg || theme.palette.grey[300],
        color: statusColors[status]?.color || theme.palette.text.primary,
        fontWeight: 'bold',
        '& .MuiChip-label': {
            padding: '0 12px',
        },
    };
});

const CurrencyText = styled(Typography)(({ theme }) => ({
    fontFamily: 'monospace',
    fontWeight: 'bold',
    color: theme.palette.success.dark,
}));

function TransactionTable({ transactions }) {
    if (!transactions || (!transactions.lender_documentation?.length && !transactions.borrower_documentation?.length)) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                    No transactions found
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Lender Documentation Section */}
            {transactions.lender_documentation?.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Received Payments
                    </Typography>
                    <TableContainer component={Paper} elevation={0}>
                        <Table sx={{ minWidth: 650 }} aria-label="lender documentation table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Date</StyledTableCell>
                                    <StyledTableCell>Listing</StyledTableCell>
                                    <StyledTableCell>Amount Total</StyledTableCell>
                                    <StyledTableCell>Platform Fee</StyledTableCell>
                                    <StyledTableCell>Net Amount</StyledTableCell>
                                    <StyledTableCell>Receipt</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.lender_documentation.map((doc, index) => (
                                    <StyledTableRow key={`lender-${index}`}>
                                        <TableCell>
                                            <Tooltip title={format(new Date(doc.payment_date), "MMM d, yyyy h:mm a")} arrow>
                                                <Typography variant="body2">
                                                    {formatDistanceToNow(new Date(doc.payment_date), { addSuffix: true })}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {doc.metadata?.listing_title || doc.listing_title || 'N/A'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                ID: {doc.transaction_id || doc.payment_id || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <CurrencyText variant="body2">
                                                ${doc.amount_total.toFixed(2)}
                                            </CurrencyText>
                                        </TableCell>
                                        <TableCell>
                                            <CurrencyText variant="body2" color="error">
                                                -${doc.platform_fee.toFixed(2)}
                                            </CurrencyText>
                                        </TableCell>
                                        <TableCell>
                                            <CurrencyText variant="body2" sx={{ color: 'primary.main' }}>
                                                ${doc.amount_net.toFixed(2)}
                                            </CurrencyText>
                                        </TableCell>
                                        <TableCell>
                                            {doc.receipt_url ? (
                                                <Tooltip title={`View receipt for ${doc.transaction_id || doc.payment_id}`} arrow>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <IconButton
                                                            href={doc.receipt_url}
                                                            target="_blank"
                                                            rel="noopener"
                                                            size="small"
                                                            color="primary"
                                                        >
                                                            <ReceiptIcon fontSize="small" />
                                                        </IconButton>
                                                        <Link
                                                            href={doc.receipt_url}
                                                            target="_blank"
                                                            rel="noopener"
                                                            underline="hover"
                                                            sx={{
                                                                fontWeight: 500,
                                                                '&:hover': {
                                                                    color: (theme) => theme.palette.primary.dark
                                                                }
                                                            }}
                                                        >
                                                            View Receipt
                                                        </Link>
                                                    </Box>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    Not Available
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Borrower Documentation Section */}
            {transactions.borrower_documentation?.length > 0 && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Payments Made
                    </Typography>
                    <TableContainer component={Paper} elevation={0}>
                        <Table sx={{ minWidth: 650 }} aria-label="borrower documentation table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Date</StyledTableCell>
                                    <StyledTableCell>Listing</StyledTableCell>
                                    <StyledTableCell>Amount</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                    <StyledTableCell>Receipt</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.borrower_documentation.map((doc, index) => (
                                    <StyledTableRow key={`borrower-${index}`}>
                                        <TableCell>
                                            <Tooltip title={format(new Date(doc.payment_date), "MMM d, yyyy h:mm a")} arrow>
                                                <Typography variant="body2">
                                                    {formatDistanceToNow(new Date(doc.payment_date), { addSuffix: true })}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {doc.metadata?.listing_title || 'N/A'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                ID: {doc.transaction_id || doc.payment_id || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <CurrencyText variant="body2">
                                                ${doc.amount.toFixed(2)}
                                            </CurrencyText>
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip
                                                label={doc.status?.toUpperCase() || 'UNKNOWN'}
                                                status={doc.status?.toLowerCase() || 'unknown'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {doc.receipt_url ? (
                                                <Tooltip title={`View receipt for ${doc.transaction_id || doc.payment_id}`} arrow>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <IconButton
                                                            href={doc.receipt_url}
                                                            target="_blank"
                                                            rel="noopener"
                                                            size="small"
                                                            color="primary"
                                                        >
                                                            <ReceiptIcon fontSize="small" />
                                                        </IconButton>
                                                        <Link
                                                            href={doc.receipt_url}
                                                            target="_blank"
                                                            rel="noopener"
                                                            underline="hover"
                                                            sx={{
                                                                fontWeight: 500,
                                                                '&:hover': {
                                                                    color: (theme) => theme.palette.primary.dark
                                                                }
                                                            }}
                                                        >
                                                            View Receipt
                                                        </Link>
                                                    </Box>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    Not Available
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </Box>
    );
}

export default TransactionTable;
